import { ExternalLink } from "lucide-react";
import { Link, useNavigate, useParams } from "react-router";
import { buttonClass } from "@/design/buttons";
import { BusinessLogo } from "@/design/BusinessLogo";
import { FavoriteHeart } from "@/design/FavoriteHeart";
import { googleMapsHref } from "@/design/maps";
import { MapLink } from "@/design/MapLink";
import { PhotoGallery } from "@/design/PhotoGallery";
import { RatingBadge } from "@/design/Rating";
import { servicePriceLine } from "@/design/servicePrice";
import { SOCIAL_PLATFORM_NAMES } from "@/modules/catalog/surfaces";
import { useMe } from "~/auth/MeProvider";
import { put } from "~/lib/api";
import { ClinicFrame, useClinic } from "./clinicProfile";

/**
 * BusinessPublicProfileScreen, the clinic **details** view
 * (docs/ux/screens/BusinessPublicProfileScreen.md). Public, no session needed
 * (H28).
 *
 * **Who the clinic is, then one button** (H59 part 5). Booking used to sit
 * under the header, so somebody reading about a clinic was already inside a
 * form with a service preselected. It is its own screen now,
 * ClinicBookingScreen, and this one holds no selection at all.
 *
 * **No practitioner anywhere (H45).** The services are the clinic's, and so
 * are the photos, the About and the links.
 */
export function BusinessPublicProfileScreen() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { me } = useMe();
  const clinic = useClinic(id);
  const signedIn = me?.kind === "consumer";

  return (
    <ClinicFrame clinic={clinic}>
      {(data) => {
        const { business, rating } = data;
        // Google Maps: the universal URL opens the native app on a phone and
        // the web map on a desktop.
        const mapHref = googleMapsHref(business.latitude, business.longitude);
        const here = `/businesses/${business.id}`;

        return (
          <>
            <Link to="/search" className="mb-4 inline-block text-sm font-medium text-accent">
              ‹ Back to search
            </Link>

            <PhotoGallery urls={business.photoUrls} name={business.name} />

            <div className="mb-6 rounded-xl border border-line bg-surface p-6 shadow-sm">
              <div className="flex items-start gap-4">
                <BusinessLogo logoUrl={business.logoUrl} name={business.name} size={64} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-3">
                    <h1 className="text-2xl font-bold text-ink">{business.name}</h1>
                    {/* H35: saving a clinic changes what this Consumer sees and
                        nothing about what they may do here. */}
                    <FavoriteHeart
                      businessId={business.id}
                      initialFavorite={data.favorite}
                      signedIn={signedIn}
                      next={here}
                      className="shrink-0"
                      push={(url) => void navigate(url)}
                      save={async (businessId, wanted) => {
                        const result = await put(`/favorites/${businessId}`, {
                          favorite: wanted,
                        });
                        return result.ok;
                      }}
                    />
                  </div>
                  <div className="mt-2">
                    <RatingBadge summary={rating} />
                  </div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {business.categoryNames.map((name) => (
                      <span
                        key={name}
                        className="rounded-lg bg-accent/10 px-3 py-1 text-xs font-semibold text-accent"
                      >
                        {name}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
              <div className="mt-4 flex flex-wrap gap-4 text-sm font-medium">
                {/* No street address is stored, only coordinates, so the
                    label carries the meaning on its own. */}
                <MapLink href={mapHref} />
                {business.website && (
                  <a href={business.website} target="_blank" rel="noreferrer" className="text-accent">
                    Website
                  </a>
                )}
              </div>
            </div>

            {business.about && (
              <section className="mb-6">
                <h2 className="mb-2 text-lg font-bold text-ink">About</h2>
                <p className="whitespace-pre-line text-body">{business.about}</p>
              </section>
            )}

            {business.socialLinks.length > 0 && (
              <section className="mb-6">
                <h2 className="mb-2 text-lg font-bold text-ink">Follow</h2>
                <div className="flex flex-wrap gap-2">
                  {business.socialLinks.map((link) => (
                    <a
                      key={link.platform}
                      href={link.url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1.5 rounded-full border border-line bg-surface px-3 py-1.5 text-sm font-semibold text-accent"
                    >
                      {SOCIAL_PLATFORM_NAMES[link.platform]}
                      <ExternalLink size={14} aria-hidden />
                    </a>
                  ))}
                </div>
              </section>
            )}

            <section className="mb-6">
              <h2 className="mb-2 text-lg font-bold text-ink">Services</h2>
              {data.bookServices.length === 0 ? (
                <p className="text-sm text-muted">This business hasn&apos;t listed its services yet.</p>
              ) : (
                <ul className="divide-y divide-line rounded-xl border border-line bg-surface">
                  {data.bookServices.map((service) => (
                    <li key={service.id} className="flex items-start justify-between gap-4 px-4 py-3">
                      <div className="min-w-0">
                        <p className="font-semibold text-ink">{service.name}</p>
                        <p className="text-sm text-muted">{service.durationDisplay}</p>
                      </div>
                      <p className="shrink-0 text-right text-sm font-semibold text-ink">
                        {servicePriceLine(
                          service.priceByLocation,
                          data.offeredLocations,
                          service.priceDisplay,
                        )}
                      </p>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <Link to={`${here}/book`} className={buttonClass("primary", "w-full")}>
              Book an appointment
            </Link>
          </>
        );
      }}
    </ClinicFrame>
  );
}
